import {
  CmpInvoiceStatusEnum,
  CmpPaymentRequestStateEnum,
} from "../../../graphql/types";

export type MemberApplicationPaymentRequestCapability = {
  invoiceId: string;
  available: boolean;
  reason?: string | null;
};

export type MemberApplicationPaymentRequestInvoiceEvidence = {
  id: string;
  amountDueInCents: number;
  invoiceStatus: CmpInvoiceStatusEnum;
  paymentRequestState?: CmpPaymentRequestStateEnum | null;
};

export type MemberApplicationPaymentRequestTarget<
  Invoice extends MemberApplicationPaymentRequestInvoiceEvidence,
> = {
  kind: "membership" | "add-on";
  invoice: Invoice;
};

export type MemberApplicationPaymentRequestBlockedTarget<
  Invoice extends MemberApplicationPaymentRequestInvoiceEvidence,
> = MemberApplicationPaymentRequestTarget<Invoice> & {
  reason: string;
};

export type MemberApplicationPaymentRequestResolution<
  Invoice extends MemberApplicationPaymentRequestInvoiceEvidence,
> = {
  alreadySent: Array<MemberApplicationPaymentRequestTarget<Invoice>>;
  blocked: Array<MemberApplicationPaymentRequestBlockedTarget<Invoice>>;
  notPayable: Array<MemberApplicationPaymentRequestTarget<Invoice>>;
  sendable: Array<MemberApplicationPaymentRequestTarget<Invoice>>;
  unverified: Array<MemberApplicationPaymentRequestTarget<Invoice>>;
};

const defaultBlockedReason =
  "This invoice cannot be emailed now because of its approval or payment status.";

/**
 * Resolves the narrow post-commit server capability for each exact invoice.
 * A missing or conflicting capability fails closed. Persisted SENT evidence
 * wins so a replay cannot expose a duplicate send, while waived/paid/void
 * invoices never become collection targets even if a malformed response says
 * `available: true`.
 */
export const resolveMemberApplicationPaymentRequestCapabilities = <
  Invoice extends MemberApplicationPaymentRequestInvoiceEvidence,
>({
  capabilities,
  invoices,
}: {
  capabilities: ReadonlyArray<MemberApplicationPaymentRequestCapability>;
  invoices: ReadonlyArray<MemberApplicationPaymentRequestTarget<Invoice>>;
}): MemberApplicationPaymentRequestResolution<Invoice> => {
  const capabilitiesByInvoiceId = capabilities.reduce<
    Map<string, MemberApplicationPaymentRequestCapability | null>
  >((result, capability) => {
    const invoiceId = capability.invoiceId.trim();

    if (!invoiceId) {
      return result;
    }

    if (result.has(invoiceId)) {
      result.set(invoiceId, null);
    } else {
      result.set(invoiceId, capability);
    }

    return result;
  }, new Map());
  const resolution: MemberApplicationPaymentRequestResolution<Invoice> = {
    alreadySent: [],
    blocked: [],
    notPayable: [],
    sendable: [],
    unverified: [],
  };

  invoices.forEach((target) => {
    if (
      target.invoice.paymentRequestState ===
      CmpPaymentRequestStateEnum.Sent
    ) {
      resolution.alreadySent.push(target);
      return;
    }

    if (
      target.invoice.invoiceStatus !== CmpInvoiceStatusEnum.Unpaid ||
      target.invoice.amountDueInCents <= 0
    ) {
      resolution.notPayable.push(target);
      return;
    }

    const capability = capabilitiesByInvoiceId.get(target.invoice.id);

    if (!capability) {
      resolution.unverified.push(target);
      return;
    }

    if (capability.available) {
      resolution.sendable.push(target);
      return;
    }

    resolution.blocked.push({
      ...target,
      reason: capability.reason?.trim() || defaultBlockedReason,
    });
  });

  return resolution;
};

export const getMemberApplicationPaymentRequestTargetLabel = (
  kind: MemberApplicationPaymentRequestTarget<MemberApplicationPaymentRequestInvoiceEvidence>["kind"]
) => (kind === "membership" ? "Membership invoice" : "Add-on invoice");
